/**
 * Centralized caching utilities for Wealthica2 data
 * Ensures consistent access to cached data across components
 *
 */

import { logger } from '@/services/LoggingService';
import { WealthicaStorage } from './storage';
import { normalizeInstitutionIdForCache } from './identifiers';

// Type definition for position data
interface Position {
  id: string;
  accountId?: string;
  accountNumber?: string;
  [key: string]: any;
}

// Type definition for the account positions map structure
interface AccountPositionsMap {
  [accountId: string]: {
    positions: Position[];
  };
}

// Constants for storage keys
const POSITIONS_CACHE_PREFIX = 'wealthica2_positions_cache';

/**
 * Generate a consistent cache key for positions
 * @param institutionId The institution ID
 * @param accountId Optional account ID if caching for a specific account
 * @returns A standardized cache key string
 */
export const getPositionsCacheKey = (institutionId: string, accountId?: string): string => {
  const normalizedId = normalizeInstitutionIdForCache(institutionId);
  return `${POSITIONS_CACHE_PREFIX}:${normalizedId}:${accountId || 'all'}`;
};

// Normalization logic moved to identifiers.ts (normalizeInstitutionIdForCache)

/**
 * Get cached positions for an institution or specific account
 * @param institutionId The institution ID
 * @param accountId Optional account ID for filtering positions
 * @returns A map of account IDs to positions, or null if not found
 */
export const getCachedPositions = (institutionId: string, accountId?: string): AccountPositionsMap | null => {
  try {
    const cacheKey = getPositionsCacheKey(institutionId, accountId);
    
    logger.debug('cache-utils', 'getCachedPositions called', {
      institutionId,
      accountId: accountId || 'all',
      cacheKey
    });
    
    // Use storage utility's internal getItem method
    const cachedData = WealthicaStorage.getItem(cacheKey);
    
    if (!cachedData) {
      logger.debug('cache-utils', 'No cached data found', {
        institutionId,
        accountId: accountId || 'all',
        cacheKey
      });
      return null;
    }
    
    const positionsMap: AccountPositionsMap = JSON.parse(cachedData);
    
    logger.debug('cache-utils', 'Retrieved cached positions', {
      institutionId,
      accountId: accountId || 'all',
      cacheKey,
      accountsInMap: Object.keys(positionsMap),
      totalPositions: Object.values(positionsMap).reduce((sum, entry) => sum + (entry.positions?.length || 0), 0)
    });
    
    // If requesting a specific account, filter the map
    if (accountId && !positionsMap[accountId]) {
      logger.debug('cache-utils', 'Specific account not found in cached positions', {
        institutionId,
        accountId,
        availableAccounts: Object.keys(positionsMap)
      });
      return null;
    }
    
    logger.debug('cache-utils', 'Retrieved positions from cache', { 
      institutionId, 
      accountId: accountId || 'all',
      cacheKey,
      accountCount: Object.keys(positionsMap).length
    });
    
    return positionsMap;
  } catch (error) {
    logger.warn('cache-utils', 'Failed to get cached positions', { 
      institutionId, 
      accountId: accountId || 'all',
      error: String(error)
    });
    return null;
  }
};

/**
 * Get all cached positions as a flat array
 * @param institutionId The institution ID
 * @returns Array of position objects, or empty array if none found
 */
export const getCachedPositionsArray = (institutionId: string): Position[] => {
  logger.debug('cache-utils', 'getCachedPositionsArray called', { institutionId });
  
  // First try with the exact institution ID
  const positionsMap = getCachedPositions(institutionId);
  
  if (!positionsMap) {
    // If we didn't find positions with the exact ID and this might be a demo account,
    // try with the normalized ID as well
    if (institutionId?.toLowerCase().includes('demo')) {
      // Try with normalized demo ID
      const normalizedMap = getCachedPositions('wealthica-demo');
      
      if (normalizedMap) {
        logger.debug('cache-utils', 'Found positions using normalized ID for demo account');
        const flatPositions = Object.values(normalizedMap)
          .flatMap(entry => entry.positions || [])
          .filter(Boolean);
          
        logger.debug('cache-utils', 'getCachedPositionsArray returning positions (normalized ID)', {
          institutionId,
          normalizedId: 'wealthica-demo',
          accountsInMap: Object.keys(normalizedMap).length,
          totalPositions: flatPositions.length
        });
        
        return flatPositions;
      }
    }
    
    logger.debug('cache-utils', 'getCachedPositionsArray - no positions map found', { institutionId });
    return [];
  }
  
  // Flatten the positions from all accounts into a single array
  const flatPositions = Object.values(positionsMap)
    .flatMap(entry => entry.positions || [])
    .filter(Boolean);
    
  logger.debug('cache-utils', 'getCachedPositionsArray returning positions', {
    institutionId,
    accountsInMap: Object.keys(positionsMap).length,
    totalPositions: flatPositions.length
  });
  
  return flatPositions;
};

/**
 * Cache positions for an institution or account
 * @param institutionId The institution ID
 * @param positions Position data to cache (either a map or array)
 * @param accountId Optional account ID if caching for a specific account
 */
export const setCachedPositions = (
  institutionId: string, 
  positions: AccountPositionsMap | Position[], 
  accountId?: string
): void => {
  try {
    const cacheKey = getPositionsCacheKey(institutionId, accountId);
    let positionsMap: AccountPositionsMap;
    
    logger.debug('cache-utils', 'setCachedPositions called', {
      institutionId,
      accountId: accountId || 'all',
      isArray: Array.isArray(positions),
      positionsType: typeof positions,
      positionsLength: Array.isArray(positions) ? (positions?.length || 0) : Object.keys(positions || {}).length,
      cacheKey
    });
    
    // Handle different input formats
    if (Array.isArray(positions)) {
      // Convert array format to map format
      if (accountId) {
        // Single account case
        positionsMap = { 
          [accountId]: { positions }
        };
      } else {
        // Group positions by account ID
        positionsMap = positions.reduce((map: AccountPositionsMap, position) => {
          // Attempt to find account ID from position object
          // Extract account ID from the appropriate position structure
          let posAccountId = position.accountId || position.accountNumber;
          
          // Check for investments array which is the primary structure
          if (!posAccountId && Array.isArray(position.investments) && position.investments.length > 0) {
            const investmentWithId = position.investments.find(inv => inv.investment);
            if (investmentWithId) {
              posAccountId = investmentWithId.investment;
            }
          }
          
          // If no accountId found, log error and skip this position
          if (!posAccountId) {
            logger.warn('cache-utils', 'Position missing accountId - skipping position', { 
              positionId: position.id || 'unknown',
              institutionId
            });
            return map; // Skip this position
          }
          
          if (!map[posAccountId]) {
            map[posAccountId] = { positions: [] };
          }
          
          map[posAccountId].positions.push(position);
          return map;
        }, {});
      }
    } else {
      // Already in map format
      positionsMap = positions;
    }
    
    logger.debug('cache-utils', 'setCachedPositions - final positionsMap structure', {
      institutionId,
      accountId: accountId || 'all',
      cacheKey,
      accountsInMap: Object.keys(positionsMap),
      totalPositions: Object.values(positionsMap).reduce((sum, entry) => sum + (entry.positions?.length || 0), 0)
    });
    
    // If updating for a specific account, merge with existing cache
    if (accountId) {
      const existingCache = getCachedPositions(institutionId);
      if (existingCache) {
        positionsMap = {
          ...existingCache,
          ...positionsMap
        };
      }
    }
    
    // FIXED: Use unified caching system for localStorage + sessionStorage persistence like accounts
    WealthicaStorage.setItem(cacheKey, JSON.stringify(positionsMap), true); // Enable localStorage persistence
    
    logger.debug('cache-utils', 'Cached positions successfully', {
      institutionId,
      accountCount: Object.keys(positionsMap).length,
      cacheKey
    });
  } catch (error) {
    logger.warn('cache-utils', 'Failed to cache positions', {
      institutionId,
      error: String(error)
    });
  }
};

/**
 * Clear cached positions for an institution or account
 * @param institutionId The institution ID
 * @param accountId Optional account ID to clear just one account's cache
 */
export const clearCachedPositions = (institutionId: string, accountId?: string): void => {
  try {
    if (accountId) {
      // Clear just one account's positions
      const allPositions = getCachedPositions(institutionId);
      if (allPositions && allPositions[accountId]) {
        delete allPositions[accountId];
        setCachedPositions(institutionId, allPositions);
      }
    } else {
      // Clear all positions for the institution
      const cacheKey = getPositionsCacheKey(institutionId);
      WealthicaStorage.removeItem(cacheKey);
      
      // Also check if there are any other position cache keys that might exist
      // with different formats or institution ID variations
      const keysToCheck = [
        // Exact match (already removed above)
        `${POSITIONS_CACHE_PREFIX}:${institutionId}:all`,
        // Special case for demo accounts
        `${POSITIONS_CACHE_PREFIX}:wealthica-demo:all`
      ];
      
      // Clean all possible variations
      keysToCheck.forEach(key => {
        WealthicaStorage.removeItem(key);
      });
    }
    
    logger.debug('cache-utils', 'Cleared positions cache', { 
      institutionId, 
      accountId: accountId || 'all' 
    });
  } catch (error) {
    logger.warn('cache-utils', 'Failed to clear positions cache', { 
      institutionId, 
      accountId: accountId || 'all',
      error: String(error)
    });
  }
};
